import { Button } from '@/components/ui/button'
import { Checkbox } from '@/components/ui/checkbox'
import Card from 'app/frontend/base/components/Card'
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'
import { Form, FormGroup } from '@/components/ui/form'
import { zodResolver } from '@hookform/resolvers/zod'
import SelectField from 'app/frontend/base/components/form-fields/SelectField'
import { CreateEventActivitiesPayload } from 'app/frontend/base/api/event'
import { useInstances } from 'app/frontend/base/config/queries'
import { activityTypes } from 'app/frontend/types/custom/ActivityType'
import { difficulties } from 'app/frontend/types/custom/Difficulty'
import { useDifficulties } from 'app/frontend/base/hooks/useDifficulties'
import { useEffect, useMemo } from 'react'
import { useForm } from 'react-hook-form'
import { z } from 'zod'
import { cn } from '@/lib/utils'
import capitalize from 'lodash/capitalize'
import {
  raidSizeLabel,
  raidSizeOptions,
} from 'app/frontend/base/helpers/raidSize'

const addActivityModes = [
  'show_instance_as_single_activity',
  'specific_encounter',
  'add_encounters_separately',
] as const

const addAtOptions = ['beginning', 'end'] as const

const activityTypeOptions = activityTypes.map((activityType) => ({
  label: capitalize(activityType),
  value: activityType,
}))

const addEventActivitiesFormSchema = z
  .object({
    activityType: z.enum(activityTypes),
    instanceId: z.string(),
    difficulty: z.enum(difficulties),
    raidSize: z.union([z.string(), z.number()]).nullable(),
    mode: z.enum(addActivityModes),
    addAt: z.enum(addAtOptions),
    encounterId: z.string().nullable(),
    allowDuplicates: z.boolean(),
  })
  .superRefine((values, context) => {
    if (values.activityType !== 'raids') return
    if (!values.instanceId) {
      context.addIssue({
        code: z.ZodIssueCode.custom,
        path: ['instanceId'],
        message: 'Required',
      })
    }
    if (values.mode === 'specific_encounter' && !values.encounterId) {
      context.addIssue({
        code: z.ZodIssueCode.custom,
        path: ['encounterId'],
        message: 'Required',
      })
    }
  })

type AddEventActivitiesFormValues = z.infer<typeof addEventActivitiesFormSchema>

type Props = {
  open: boolean
  setOpen: (open: boolean) => void
  event: wowaudit.Event
  isPending: boolean
  onSubmit: (payload: CreateEventActivitiesPayload) => Promise<void>
}

const AddEventActivitiesDialog = ({
  open,
  setOpen,
  event,
  isPending,
  onSubmit,
}: Props) => {
  const { data: instances } = useInstances()
  const {
    difficulties: availableDifficulties,
    defaultDifficulty,
    gameVersion,
  } = useDifficulties()
  const splitRaidSizeFields = gameVersion === 'classic_progression'
  const allowTypeSelection = event.activities.length === 0
  const defaultActivityType = allowTypeSelection ? 'custom' : 'raids'
  const dialogTitle = allowTypeSelection ? 'Add encounter' : 'Add encounters'
  const submitLabel = allowTypeSelection ? 'Add encounter' : 'Add encounters'
  const form = useForm<AddEventActivitiesFormValues>({
    resolver: zodResolver(addEventActivitiesFormSchema),
    defaultValues: {
      activityType: defaultActivityType,
      instanceId: '',
      difficulty: defaultDifficulty,
      raidSize: null,
      mode: 'show_instance_as_single_activity',
      addAt: 'end',
      encounterId: null,
      allowDuplicates: false,
    },
  })
  const values = form.watch()
  const selectedInstance = useMemo(() => {
    return instances?.find(
      (instance) => String(instance.id) === String(values.instanceId),
    )
  }, [instances, values.instanceId])
  const isSingleEncounterInstance = selectedInstance?.encounters.length === 1
  const encounterOptions = useMemo(() => {
    return (
      selectedInstance?.encounters.map((encounter) => ({
        label: encounter.name,
        value: String(encounter.id),
      })) ?? []
    )
  }, [selectedInstance])
  const instanceOptions = useMemo(() => {
    return (
      instances?.map((instance) => ({
        sortLabel: '',
        label: instance.name,
        value: String(instance.id),
      })) ?? []
    )
  }, [instances])
  const existingActivityKeys = useMemo(() => {
    return new Set(
      event.activities.map(
        (activity) =>
          `${activity.difficulty}:${activity.instance?.id ?? 0}:${
            activity.encounter?.id ?? 0
          }:${activity.raidSize}`,
      ),
    )
  }, [event.activities])
  const candidateKeys = useMemo(() => {
    if (!selectedInstance || !values.difficulty) return []
    const raidSizeKey = values.raidSize ?? selectedInstance.raidSize
    if (values.mode === 'show_instance_as_single_activity') {
      return [`${values.difficulty}:${selectedInstance.id}:0:${raidSizeKey}`]
    }
    if (values.mode === 'specific_encounter') {
      const hasEncounter = encounterOptions.some(
        (option) => option.value === values.encounterId,
      )
      if (!values.encounterId || !hasEncounter) return []
      return [
        `${values.difficulty}:${selectedInstance.id}:${values.encounterId}:${raidSizeKey}`,
      ]
    }
    return selectedInstance.encounters.map(
      (encounter) =>
        `${values.difficulty}:${selectedInstance.id}:${encounter.id}:${raidSizeKey}`,
    )
  }, [
    encounterOptions,
    selectedInstance,
    values.difficulty,
    values.mode,
    values.encounterId,
    values.raidSize,
  ])
  const duplicateCount = useMemo(() => {
    return candidateKeys.filter((key) => existingActivityKeys.has(key)).length
  }, [candidateKeys, existingActivityKeys])
  const totalToAdd = values.allowDuplicates
    ? candidateKeys.length
    : candidateKeys.length - duplicateCount

  useEffect(() => {
    if (!open) {
      form.reset({
        activityType: defaultActivityType,
        instanceId: '',
        difficulty: defaultDifficulty,
        raidSize: null,
        mode: 'show_instance_as_single_activity',
        addAt: 'end',
        encounterId: null,
        allowDuplicates: false,
      })
    }
  }, [form, open])

  const instanceField = (
    <SelectField
      name='instanceId'
      options={instanceOptions}
      transformOptions={false}
      layout='stacked'
      inModal={true}
      customLabel='Instance'
      customPlaceholder='Select an instance'
      onChange={(value) => {
        const nextInstance = instances?.find(
          (instance) => String(instance.id) === String(value),
        )
        if (nextInstance?.encounters.length === 1) {
          form.setValue('mode', 'add_encounters_separately', {
            shouldDirty: true,
          })
        } else if (isSingleEncounterInstance) {
          form.setValue('mode', 'show_instance_as_single_activity', {
            shouldDirty: true,
          })
        }
        if (form.getValues('encounterId') !== null) {
          form.setValue('encounterId', null, {
            shouldDirty: true,
          })
        }
        if (form.getValues('raidSize') !== null) {
          form.setValue('raidSize', null, {
            shouldDirty: true,
          })
        }
      }}
    />
  )

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>{dialogTitle}</DialogTitle>
        </DialogHeader>
        <Form {...form}>
          <div className='mt-4'>
            {allowTypeSelection ? (
              <SelectField
                name='activityType'
                options={activityTypeOptions}
                transformOptions={false}
                layout='stacked'
                inModal={true}
                className='mb-2'
                customLabel='Activity type'
              />
            ) : null}
            {values.activityType === 'raids' ? (
              <>
                {splitRaidSizeFields ? instanceField : null}
                <FormGroup className={splitRaidSizeFields ? 'mt-2' : undefined}>
                  {splitRaidSizeFields ? null : instanceField}
                  <SelectField
                    name='difficulty'
                    options={availableDifficulties}
                    layout='stacked'
                    inModal={true}
                    className={splitRaidSizeFields ? undefined : 'w-48'}
                    customLabel='Difficulty'
                  />
                  {selectedInstance?.altRaidSize ? (
                    <SelectField
                      name='raidSize'
                      options={raidSizeOptions(selectedInstance)}
                      transformOptions={false}
                      layout='stacked'
                      inModal={true}
                      customLabel='Raid size'
                      customPlaceholder={raidSizeLabel(
                        selectedInstance.raidSize,
                      )}
                    />
                  ) : null}
                </FormGroup>
                <FormGroup className='mt-2'>
                  <SelectField
                    name='mode'
                    options={[
                      {
                        label: 'Add instance as single encounter',
                        value: 'show_instance_as_single_activity',
                      },
                      {
                        label: 'Add specific encounter',
                        value: 'specific_encounter',
                      },
                      {
                        label: isSingleEncounterInstance
                          ? 'Add specific encounter'
                          : 'Add encounters separately',
                        value: 'add_encounters_separately',
                      },
                    ]}
                    transformOptions={false}
                    layout='stacked'
                    inModal={true}
                    disabled={isSingleEncounterInstance}
                    customLabel='&nbsp;'
                    onChange={(value) => {
                      if (value !== 'specific_encounter') {
                        if (form.getValues('encounterId') !== null) {
                          form.setValue('encounterId', null, {
                            shouldDirty: true,
                          })
                        }
                      }
                    }}
                  />
                  <SelectField
                    name='addAt'
                    options={[
                      { label: 'Beginning', value: 'beginning' },
                      { label: 'End', value: 'end' },
                    ]}
                    transformOptions={false}
                    layout='stacked'
                    inModal={true}
                    customLabel='Add at the'
                    className='w-48'
                  />
                </FormGroup>
                {values.mode === 'specific_encounter' ? (
                  <div className='mt-2'>
                    <SelectField
                      name='encounterId'
                      options={encounterOptions}
                      transformOptions={false}
                      layout='stacked'
                      inModal={true}
                      customLabel='Encounter'
                      customPlaceholder='Select an encounter'
                    />
                  </div>
                ) : null}

                <label
                  className={cn(
                    'flex items-center gap-2 my-4 cursor-pointer w-fit',
                    duplicateCount > 0 ? 'visible' : 'hidden',
                  )}
                >
                  <Checkbox
                    checked={values.allowDuplicates}
                    onCheckedChange={(value) =>
                      form.setValue('allowDuplicates', value === true, {
                        shouldDirty: true,
                      })
                    }
                  />
                  Allow duplicate encounters
                </label>
              </>
            ) : null}

            <div className='flex justify-between mt-2 gap-2 items-center text-xs'>
              {values.activityType === 'raids' && values.instanceId ? (
                <Card className='px-3 py-2 flex items-center gap-3'>
                  To add:{' '}
                  <span
                    className={cn(
                      'font-semibold',
                      totalToAdd > 0
                        ? 'text-success-foreground'
                        : 'text-warning-foreground',
                    )}
                  >
                    {totalToAdd}
                  </span>
                  <div className='h-4 w-px bg-border' />
                  Already exists:{' '}
                  {values.allowDuplicates ? (
                    <span className='font-semibold opacity-50'>-</span>
                  ) : (
                    <span className='font-semibold'>{duplicateCount}</span>
                  )}
                </Card>
              ) : (
                <div />
              )}

              <div className='flex justify-end gap-2 items-center'>
                <Button
                  type='button'
                  size='sm'
                  variant='outline'
                  onClick={() => setOpen(false)}
                  disabled={isPending}
                >
                  Cancel
                </Button>
                <Button
                  type='button'
                  size='sm'
                  disabled={values.activityType === 'raids' && totalToAdd <= 0}
                  loading={isPending}
                  onClick={form.handleSubmit(async (nextValues) => {
                    if (nextValues.activityType === 'raids') {
                      await onSubmit({
                        activityType: 'raids',
                        addAt: nextValues.addAt,
                        instanceId: Number(nextValues.instanceId),
                        difficulty: nextValues.difficulty,
                        raidSize:
                          nextValues.raidSize != null
                            ? Number(nextValues.raidSize)
                            : undefined,
                        mode: nextValues.mode,
                        encounterId: nextValues.encounterId
                          ? Number(nextValues.encounterId)
                          : undefined,
                        allowDuplicates: nextValues.allowDuplicates,
                      })
                    } else {
                      await onSubmit({
                        activityType: nextValues.activityType,
                        addAt: nextValues.addAt,
                      })
                    }
                    setOpen(false)
                  })}
                >
                  {submitLabel}
                </Button>
              </div>
            </div>
          </div>
        </Form>
      </DialogContent>
    </Dialog>
  )
}

export default AddEventActivitiesDialog
